/**
 * ประลองยุทธภพ, the offline weekly tournament (C0-TOURNEY; data/tournament.ts, swiss.ts, systems/tournamentRun.ts).
 * This is the World side: the in-game week and its two gates (Swiss on Saturday 20:00, the top four on Sunday 20:00),
 * the herald beside the ring, sign-up, the player's real bouts (best of three at the ring, fair-arena rules, through
 * `DuelMatchSystem.startTourney`), the rewards, and the holder of the legendary golden dragon palm.
 *
 * - Every other bout is decided by the run's own seeded stream, so the event is deterministic and resumable; the run is
 *   saved in the progress (`tourney`).
 * - The champion holds the palm until the next crowning (a player gets the art; a wanderer uses it in the world, see
 *   systems/wanderers.ts `palm`). Three weeks in a row make a guest grandmaster who sits out the next week.
 * - The clock is the World's hour; a day is counted when the hour wraps. Day 0 is a Monday.
 * - `test` starts the short 16-entrant, 4-round event at once: it pays no coins, awards no palm and enters no hall.
 */
import { DUEL_STYLES } from '../data/duelMatch';
import { ladderFor } from '../data/ladder';
import type { HolderRecord } from '../data/legendary';
import { SPAR_TIERS } from '../data/sparring';
import {
  HERALD_AT,
  TOURNEY,
  TOURNEY_COINS,
  TOURNEY_GUESTS,
  TOURNEY_MODES,
  TOURNEY_TEXT,
  TOURNEY_TITLES,
  TOURNEY_ART,
  type TourneyModeId,
} from '../data/tournament';
import { WANDERER_NAMES, WANDERER_SCHOOLS, type Personality } from '../data/wanderers';
import { getArt } from '../data/skills';
import { plains256 } from '../data/zones';
import { freeAt, nearestFloor } from '../path';
import { mulberry32 } from '../rng';
import type { Entity, World } from '../world';
import { duelMatchOf } from './duelMatch';
import {
  entrantOf,
  forfeitMine,
  makeRun,
  pendingFor,
  placeOf,
  resolveAll,
  scoreOf,
  settle,
  startBracket,
  swissStandings,
  type BoutRec,
  type TourneyEntrant,
  type TourneyRun,
} from './tournamentRun';
import type { SystemHost, WorldSystem } from './types';
import { wanderersOf } from './wanderers';

// ───────────────────────────── commands, events, the save ─────────────────────────────

export type TourneyOp = 'signup' | 'withdraw' | 'fight' | 'exhibition' | 'test' | 'abandon';
export type TourneyCommand = { t: 'tourney'; op: TourneyOp };
export type TourneyEvent = {
  type: 'tourney';
  /** `open`: the herald was spoken to (open the panel); the rest tell the panel to refresh. */
  what: 'open' | 'signup' | 'start' | 'round' | 'bout' | 'cut' | 'done' | 'palm';
};

export interface HallEntry {
  /** Week number as shown (the first is 1). */
  readonly week: number;
  readonly name: string;
  readonly who: 'player' | 'wanderer' | 'other';
  /** Swiss wins of the champion that week, and weeks in a row. */
  readonly wins: number;
  readonly streak: number;
}

export interface TourneySave {
  /** The in-game clock of the event: whole days counted and the hour last seen. */
  clock?: { day: number; hour: number };
  /** The week the player signed up for, the last week a run was made, a week the holder sits out. */
  signed?: number;
  ran?: number;
  sitOut?: { who: HolderRecord['who']; id: string; week: number };
  coins: number;
  hall: HallEntry[];
  holder?: HolderRecord;
  titles: string[];
  entered: number;
  run?: TourneyRun;
  /** The bye round the exhibition was fought for (week:round), so it is shown once. */
  exh?: string;
}

export const newTourneySave = (): TourneySave => ({ coins: 0, hall: [], titles: [], entered: 0 });

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const num = (v: unknown, d = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : d);

/** A saved tournament kept within its shape (a broken run is dropped, the rest kept). */
export function parseTourney(v: unknown): TourneySave {
  const out = newTourneySave();
  if (!isObj(v)) return out;
  out.coins = Math.max(0, Math.floor(num(v.coins)));
  out.entered = Math.max(0, Math.floor(num(v.entered)));
  if (isObj(v.clock)) out.clock = { day: Math.max(0, Math.floor(num(v.clock.day))), hour: num(v.clock.hour, 8) };
  if (typeof v.signed === 'number') out.signed = v.signed;
  if (typeof v.ran === 'number') out.ran = v.ran;
  if (typeof v.exh === 'string') out.exh = v.exh;
  if (Array.isArray(v.titles)) out.titles = v.titles.filter((x): x is string => typeof x === 'string').slice(-40);
  if (Array.isArray(v.hall))
    out.hall = v.hall
      .filter((h): h is Record<string, unknown> => isObj(h) && typeof h.name === 'string')
      .map((h) => ({
        week: Math.floor(num(h.week, 1)),
        name: String(h.name),
        who: (h.who === 'player' || h.who === 'wanderer' ? h.who : 'other') as HallEntry['who'],
        wins: Math.floor(num(h.wins)),
        streak: Math.max(1, Math.floor(num(h.streak, 1))),
      }))
      .slice(-TOURNEY.hallMax);
  const hd = v.holder;
  if (isObj(hd) && typeof hd.name === 'string' && typeof hd.id === 'string')
    out.holder = {
      art: TOURNEY_ART,
      who: hd.who === 'player' || hd.who === 'wanderer' ? hd.who : 'other',
      id: hd.id,
      name: hd.name,
      week: Math.floor(num(hd.week)),
      streak: Math.max(1, Math.floor(num(hd.streak, 1))),
    };
  const so = v.sitOut;
  if (isObj(so) && typeof so.id === 'string')
    out.sitOut = { who: so.who === 'player' || so.who === 'wanderer' ? so.who : 'other', id: so.id, week: Math.floor(num(so.week)) };
  const r = v.run;
  if (isObj(r) && Array.isArray(r.entrants) && isObj(r.swiss) && Array.isArray(r.swiss.players) && typeof r.mode === 'string' && r.mode in TOURNEY_MODES)
    out.run = r as unknown as TourneyRun;
  return out;
}

// ───────────────────────────── the field ─────────────────────────────

const STYLE_OF: Readonly<Record<Personality, string>> = {
  hot: 'fierce',
  calm: 'calm',
  shy: 'calm',
  sly: 'sly',
  playful: 'sly',
  trader: 'calm',
};
const levelFor = (rating: number): number => Math.max(0, Math.min(4, Math.round((rating - 700) / 250)));

const SCHOOLS = 6;

/** The 32 (or 16) entrants of an event, the same each time for the same World and week. */
export function makeField(
  w: World,
  mode: TourneyModeId,
  week: number,
  playerIn: boolean,
  sit: TourneySave['sitOut'],
): TourneyEntrant[] {
  const m = TOURNEY_MODES[mode];
  const rnd = mulberry32((((w.map.seed | 0) * 2654435761) ^ (week * 40503) ^ (mode === 'test' ? 77 : 5)) >>> 0);
  const pick = <T,>(xs: readonly T[]): T => xs[Math.floor(rnd() * xs.length)]!;
  const out: TourneyEntrant[] = [];
  const spars = SPAR_TIERS.length;
  const wanderN = m.entrants - 1 - spars;
  let id = 1;
  const pr = w.progress;
  const slot = (i: number): TourneyEntrant => {
    if (playerIn)
      return {
        id: i,
        name: pr.name,
        kind: 'player',
        idx: 0,
        rating: pr.ladder?.[ladderFor(pr.karma)]?.rating ?? 1000,
        style: 'fierce',
        level: 2,
        sprite: '',
      };
    const name = TOURNEY_GUESTS[(week + (mode === 'test' ? 1 : 0)) % TOURNEY_GUESTS.length]!;
    return { id: i, name, kind: 'guest', idx: 0, rating: 1050, style: pick(DUEL_STYLES).id, level: 2, sprite: 'heroBlood', guild: 'guest' };
  };
  out.push(slot(id++));
  const minds = wanderersOf(w)?.minds ?? [];
  const used = new Set<string>([pr.name]);
  for (let i = 0; i < wanderN; i++) {
    const mind = minds[i];
    const sitting = !!sit && sit.who === 'wanderer' && sit.week === week && sit.id === `w${i}`;
    if (mind && !sitting) {
      const d = mind.def;
      const rating = Math.round(1000 + ((d.strength - 0.75) / 0.55) * 600);
      out.push({
        id: id++,
        name: d.name,
        kind: 'wanderer',
        idx: i,
        rating,
        style: STYLE_OF[d.personality],
        level: levelFor(rating),
        sprite: d.sprite,
        guild: `s${i % SCHOOLS}`,
      });
      used.add(d.name);
      continue;
    }
    const female = i % 2 === 1;
    const school = WANDERER_SCHOOLS[i % SCHOOLS]!;
    const N = WANDERER_NAMES;
    let name = '';
    for (let k = 0; k < 20 && (!name || used.has(name)); k++) name = pick(N.family) + pick(female ? N.female : N.male);
    used.add(name);
    const rating = Math.round(1000 + rnd() * 600);
    out.push({
      id: id++,
      name,
      kind: sitting ? 'guest' : 'wanderer',
      idx: i,
      rating,
      style: pick(DUEL_STYLES).id,
      level: levelFor(rating),
      sprite: female ? school.female : school.male,
      guild: `s${i % SCHOOLS}`,
    });
  }
  SPAR_TIERS.forEach((t, k) => {
    const rating = 800 + k * 150;
    out.push({
      id: id++,
      name: t.person.name,
      kind: 'spar',
      idx: k,
      rating,
      style: DUEL_STYLES[k % DUEL_STYLES.length]!.id,
      level: levelFor(rating),
      sprite: t.person.sprite,
      guild: `p${k % 2}`,
    });
  });
  return out;
}

// ───────────────────────────── the clock ─────────────────────────────

const hoursOf = (c: { day: number; hour: number }): number => (c.day % 7) * 24 + c.hour;
const SWISS_T = TOURNEY.swissAt.day * 24 + TOURNEY.swissAt.hour;
const CUT_T = TOURNEY.cutAt.day * 24 + TOURNEY.cutAt.hour;
const SIGNUP_END = TOURNEY.signupTo.day * 24 + TOURNEY.signupTo.hour;

/** The Thai name of a weekday (0 Monday). */
export const WEEKDAY_NAMES = ['จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์', 'อาทิตย์'] as const;

// ───────────────────────────── the view ─────────────────────────────

export interface TourneyRow {
  readonly id: number;
  readonly rank: number;
  readonly name: string;
  readonly kind: TourneyEntrant['kind'];
  readonly score: number;
  readonly buchholz: number;
  readonly me: boolean;
}
export interface TourneyBoutView {
  readonly a: string;
  readonly b: string | null;
  readonly aId: number;
  readonly bId: number | null;
  readonly winner: number | null;
  readonly mine: boolean;
}
export interface TourneyView {
  /** What the panel shows: before the event, signed up, the Swiss, between the stages, the knock-out, or the finished event. */
  readonly stage: 'none' | 'swiss' | 'swissDone' | 'bracket' | 'done';
  readonly mode: TourneyModeId | null;
  readonly week: number;
  readonly day: number;
  readonly hour: number;
  readonly signupOpen: boolean;
  readonly signed: boolean;
  readonly sittingOut: boolean;
  /** Hours of game time to the next gate (the Swiss start, the knock-out start), or null. */
  readonly nextGate: { readonly label: string; readonly hours: number } | null;
  readonly round: number;
  readonly rounds: number;
  readonly inField: boolean;
  /** The player's bout to fight now. */
  readonly mine: { readonly opponent: string; readonly label: string; readonly kind: 'swiss' | 'bracket' } | null;
  /** The player has a bye this round and may take the exhibition. */
  readonly bye: boolean;
  readonly canExhibit: boolean;
  readonly standings: readonly TourneyRow[];
  readonly pairs: readonly TourneyBoutView[];
  readonly bracket: readonly (readonly TourneyBoutView[])[];
  readonly places: readonly string[];
  readonly myPlace: number;
  readonly holder: HolderRecord | null;
  readonly hall: readonly HallEntry[];
  readonly coins: number;
  readonly titles: readonly string[];
  readonly fighting: boolean;
}

const SYSTEMS = new WeakMap<object, TournamentSystem>();
export const tourneyOf = (w: object): TournamentSystem | null => SYSTEMS.get(w) ?? null;

export class TournamentSystem implements WorldSystem {
  readonly name = 'tourney';
  heraldId = -1;
  private waiting: { rec: BoutRec; exhibition: boolean } | null = null;
  private tick = 0;
  private palmT = 0;
  private placed = false;

  constructor(private readonly h: SystemHost) {}

  readonly commands = { tourney: (c: { op: TourneyOp }) => this.op(c.op) };

  private get save(): TourneySave {
    const pr = this.h.world.progress;
    return (pr.tourney ??= newTourneySave());
  }

  setup(): void {
    SYSTEMS.set(this.h.world, this);
  }

  /** The herald stands at the ring, put there on the first step (after every other system's own people are in). */
  private placeHerald(): void {
    this.placed = true;
    const w = this.h.world;
    if (w.map.zone !== 'plains' || w.map.interior || !plains256()) return;
    const want = HERALD_AT;
    const at = freeAt(w.map, want[0], want[1])
      ? { x: want[0], y: want[1] }
      : (() => {
          const f = nearestFloor(w.map, want[0], want[1], 6);
          return f ? { x: f[0], y: f[1] } : null;
        })();
    if (!at) return;
    const e = this.h.add({ kind: 'npc', x: at.x, y: at.y, name: TOURNEY_TEXT.herald, title: TOURNEY_TEXT.heraldTitle, sprite: 'guardOfficer', facing: 1 });
    this.heraldId = e.id;
  }

  talk(npc: Entity): boolean {
    if (npc.id !== this.heraldId || this.heraldId < 0) return false;
    this.h.emit({ type: 'tourney', what: 'open' });
    return true;
  }

  // ─────────────── commands ───────────────

  private clock(): { day: number; hour: number } {
    const sv = this.save;
    return (sv.clock ??= { day: 0, hour: this.h.world.hour });
  }

  private weekNo(): number {
    return Math.floor(this.clock().day / 7);
  }

  private sittingOut(): boolean {
    const so = this.save.sitOut;
    return !!so && so.who === 'player' && so.week === this.weekNo();
  }

  private op(op: TourneyOp): void {
    const w = this.h.world;
    if (w.player.id !== w.primaryId) return;
    const sv = this.save;
    const run = sv.run;
    switch (op) {
      case 'signup': {
        if (this.sittingOut()) return this.h.notice('ท่านเป็นปรมาจารย์รับเชิญ พักการแข่งสัปดาห์นี้');
        if (hoursOf(this.clock()) >= SIGNUP_END && !(run?.mode === 'test')) return this.h.notice('ปิดรับสมัครแล้ว (เสาร์ 19:30)');
        sv.signed = this.weekNo();
        this.h.notice(`สมัคร${TOURNEY_TEXT.name}สัปดาห์นี้แล้ว · รอบสวิสเริ่มเสาร์ 20:00`);
        this.h.emit({ type: 'tourney', what: 'signup' });
        return;
      }
      case 'withdraw': {
        if (sv.signed === this.weekNo() && !(run && run.mode === 'week' && run.week === this.weekNo())) {
          delete sv.signed;
          this.h.notice('ถอนชื่อออกแล้ว');
          this.h.emit({ type: 'tourney', what: 'signup' });
        }
        return;
      }
      case 'test': {
        if (run && run.phase !== 'done' && run.mode === 'week' && run.me) return this.h.notice('กำลังแข่งประลองยุทธภพจริงอยู่');
        if (this.waiting || duelMatchOf(w)?.active) return this.h.notice('อยู่ระหว่างประลองอยู่แล้ว');
        this.startRun('test');
        return;
      }
      case 'abandon': {
        if (!run || run.phase === 'done' || !run.me) return;
        if (this.waiting || duelMatchOf(w)?.active) return this.h.notice('จบนัดที่กำลังสู้ก่อน');
        resolveAll(run, true);
        this.afterChange(run);
        this.h.notice('ถอนตัวจากการแข่งที่เหลือ');
        return;
      }
      case 'fight':
        return this.fight(false);
      case 'exhibition':
        return this.fight(true);
    }
  }

  private fight(exhibition: boolean): void {
    const sv = this.save;
    const run = sv.run;
    if (!run || !run.me) return this.h.notice('ท่านไม่ได้อยู่ในการแข่งตอนนี้');
    if (this.waiting) return;
    const d = duelMatchOf(this.h.world);
    if (!d) return this.h.notice('ที่นี่ประลองไม่ได้');
    let rec: BoutRec | null;
    let opp: TourneyEntrant;
    let label: string;
    if (exhibition) {
      const key = `${run.week}:${run.swiss.round}`;
      const bye = run.phase === 'swiss' ? run.pairs.find((r) => r.b === null && r.a === run.me) : undefined;
      if (!bye || sv.exh === key) return this.h.notice('ไม่มีประลองโชว์ให้ตอนนี้');
      const ws = run.entrants.filter((e) => e.kind === 'wanderer');
      opp = ws[(run.week + run.swiss.round) % Math.max(1, ws.length)] ?? run.entrants[1]!;
      rec = bye;
      label = `ประลองโชว์ (${TOURNEY_TEXT.bye})`;
    } else {
      rec = pendingFor(run, run.me);
      if (!rec || rec.b === null) return this.h.notice(run.phase === 'swiss' ? 'รอบนี้ท่านได้บาย · รับประลองโชว์ได้' : 'ไม่มีนัดของท่านตอนนี้');
      opp = entrantOf(run, rec.a === run.me ? rec.b : rec.a);
      label = run.phase === 'swiss' ? `รอบสวิส ${run.swiss.round}/${run.swiss.rounds}` : `แพ้คัดออก รอบ ${run.bracket.length}`;
    }
    const why = d.startTourney({ name: opp.name, styleId: opp.style, level: opp.level, sprite: opp.sprite || 'heroBlood', label, exhibition });
    if (why) return this.h.notice(why);
    this.waiting = { rec, exhibition };
    this.h.emit({ type: 'tourney', what: 'bout' });
  }

  // ─────────────── runs ───────────────

  private startRun(mode: TourneyModeId): void {
    const w = this.h.world;
    const sv = this.save;
    const wk = this.weekNo();
    const sit = sv.sitOut;
    const playerIn = mode === 'test' ? true : sv.signed === wk && !this.sittingOut();
    const seed = ((w.map.seed | 0) * 31 + wk * 7919 + (mode === 'test' ? 4001 + sv.entered * 17 : 0)) | 0;
    const entrants = makeField(w, mode, wk, playerIn, sit);
    const run = makeRun({ mode, week: wk, seed, me: playerIn ? 1 : 0, entrants });
    sv.run = run;
    if (mode === 'week') sv.ran = wk;
    if (playerIn && mode === 'week') sv.entered++;
    this.waiting = null;
    this.h.notice(playerIn ? `${TOURNEY_TEXT.name}เริ่มแล้ว · ${entrants.length} คน · พบ${TOURNEY_TEXT.herald}ที่ลานประลอง` : `${TOURNEY_TEXT.name}ประจำสัปดาห์เริ่มแล้ว`);
    this.h.emit({ type: 'tourney', what: 'start' });
    this.afterChange(run);
  }

  /** After any change to a run: settle what no one is waiting for, and finish if it is done. */
  private afterChange(run: TourneyRun): void {
    if (run.phase === 'done') return this.finishRun(run);
    if (run.phase === 'swissDone') this.h.emit({ type: 'tourney', what: 'round' });
    this.h.emit({ type: 'tourney', what: 'round' });
  }

  private finishRun(run: TourneyRun): void {
    const sv = this.save;
    if (run.paid) return;
    run.paid = true;
    const champion = entrantOf(run, run.places[0]!);
    if (run.me) {
      const place = placeOf(run, run.me);
      if (run.mode === 'week') {
        const wins = scoreOf(run, run.me);
        const coins = TOURNEY_COINS.entry + wins * TOURNEY_COINS.swissWin + (place ? (TOURNEY_COINS.place[place - 1] ?? 0) : 0);
        sv.coins += coins;
        if (place) {
          const t = TOURNEY_TITLES[Math.min(place, TOURNEY_TITLES.length) - 1]!.replace('{w}', String(run.week + 1));
          sv.titles.push(t);
          this.h.notice(`ฉายา: ${t}`);
        }
        this.h.log('sys', `${TOURNEY_TEXT.name} จบแล้ว · ${place ? `ได้อันดับ ${place}` : 'ตกรอบ'} · ${TOURNEY_TEXT.coins} +${coins}`);
      } else this.h.log('sys', `${TOURNEY_TEXT.name} (รอบซ้อม) จบแล้ว · ${place ? `ได้อันดับ ${place}` : 'ตกรอบ'}`);
    }
    if (run.mode === 'week') {
      const prev = sv.holder;
      const who: HolderRecord['who'] = champion.kind === 'player' ? 'player' : champion.kind === 'wanderer' ? 'wanderer' : 'other';
      const id = champion.kind === 'player' ? 'player' : champion.kind === 'wanderer' ? `w${champion.idx}` : champion.name;
      const streak = prev && prev.who === who && prev.id === id ? prev.streak + 1 : 1;
      sv.holder = { art: TOURNEY_ART, who, id, name: champion.name, week: run.week, streak };
      sv.hall.push({ week: run.week + 1, name: champion.name, who, wins: scoreOf(run, champion.id), streak });
      if (sv.hall.length > TOURNEY.hallMax) sv.hall.splice(0, sv.hall.length - TOURNEY.hallMax);
      if (streak >= TOURNEY.streakForGuest) {
        sv.sitOut = { who, id, week: run.week + 1 };
        if (who === 'player') this.h.notice(`ท่านชนะติดกัน ${streak} สัปดาห์ เป็น${TOURNEY_TEXT.guest} พักการแข่งสัปดาห์หน้า`);
      }
      this.h.notice(who === 'player' ? `ท่านคือ${TOURNEY_TEXT.holder} ถือ${getArt(TOURNEY_ART).name}หนึ่งสัปดาห์` : `${champion.name} ได้เป็น${TOURNEY_TEXT.holder}`);
      this.syncPalm();
    }
    this.h.emit({ type: 'tourney', what: 'done' });
  }

  // ─────────────── the palm ───────────────

  /** Puts the palm where the holder record says: the player's arts, or a wanderer's hands. */
  syncPalm(): void {
    const w = this.h.world;
    const pr = w.progress;
    const hd = this.save.holder;
    const mine = hd?.who === 'player';
    const has = pr.arts.includes(TOURNEY_ART);
    if (mine && !has) {
      pr.arts.push(TOURNEY_ART);
      this.h.emit({ type: 'progressChanged' });
      this.h.emit({ type: 'tourney', what: 'palm' });
    } else if (!mine && has) {
      if (pr.activeArt === TOURNEY_ART) {
        const other = pr.arts.find((a) => a !== TOURNEY_ART);
        if (other && !w.player.cast) w.apply({ t: 'setArt', id: other });
      }
      if (pr.activeArt !== TOURNEY_ART) {
        pr.arts = pr.arts.filter((a) => a !== TOURNEY_ART);
        if (pr.skillBar) {
          const ids = new Set(getArt(TOURNEY_ART).skills);
          pr.skillBar = pr.skillBar.map((s) => (s && ids.has(s) ? null : s));
        }
        this.h.emit({ type: 'progressChanged' });
        this.h.emit({ type: 'tourney', what: 'palm' });
        this.h.notice(`${getArt(TOURNEY_ART).name}กลับสู่ยุทธภพ`);
      }
    }
    for (const m of wanderersOf(w)?.minds ?? []) {
      const on = hd?.who === 'wanderer' && hd.id === m.def.id;
      m.palm = on;
      if (m.e) m.e.title = on ? `[${TOURNEY_TEXT.holder}] ${m.def.nick}` : `${'[พเนจร]'} ${m.def.nick}`;
    }
  }

  // ─────────────── the clock and the polls ───────────────

  step(dt: number): void {
    if (!this.placed) this.placeHerald();
    this.tick += dt;
    if (this.tick < 0.5) return;
    this.tick = 0;
    const w = this.h.world;
    if (w.player.id !== w.primaryId) return;
    // Nothing to keep until the herald is there or the player has used the tournament (no save is made for a World without it).
    if (this.heraldId < 0 && !w.progress.tourney) return;
    const sv = this.save;
    const c = this.clock();
    const hr = w.hour;
    if (hr < c.hour - 12) c.day++;
    c.hour = hr;
    this.poll();
    this.gates();
    if ((this.palmT -= 0.5) <= 0) {
      this.palmT = 4;
      if (sv.holder || w.progress.arts.includes(TOURNEY_ART)) this.syncPalm();
    }
  }

  /** The player's bout in the ring has an answer: record it. */
  private poll(): void {
    const wt = this.waiting;
    if (!wt) return;
    const cur = duelMatchOf(this.h.world)?.current;
    if (!cur?.tourney) {
      this.waiting = null;
      return;
    }
    if (cur.phase !== 'result') return;
    const run = this.save.run;
    this.waiting = null;
    if (!run) return;
    const won = cur.winner === 0;
    if (wt.exhibition) {
      this.save.exh = `${run.week}:${run.swiss.round}`;
      if (run.mode === 'week') this.save.coins += TOURNEY.exhibitionCoins;
      this.h.notice(won ? 'ประลองโชว์: ท่านชนะ' : 'ประลองโชว์: ท่านแพ้ (ไม่มีผลต่อการแข่ง)');
      this.h.emit({ type: 'tourney', what: 'bout' });
      return;
    }
    const rec = wt.rec;
    const meId = run.me;
    settle(run, rec, won ? meId : rec.a === meId ? rec.b! : rec.a);
    this.h.notice(won ? 'ชนะนัดนี้' : 'แพ้นัดนี้');
    this.afterChange(run);
    // The rest of the stage may now be waiting only on the clock.
    this.gates();
  }

  private gates(): void {
    const sv = this.save;
    const wk = this.weekNo();
    const t = hoursOf(this.clock());
    let run = sv.run;
    if (this.waiting) return;
    // A weekly run is due: Saturday 20:00 of a week that has none yet.
    if (sv.ran !== wk && t >= SWISS_T) {
      if (run && run.phase !== 'done') {
        resolveAll(run, true);
        this.afterChange(run);
      }
      this.startRun('week');
      run = sv.run;
    }
    if (!run || run.mode !== 'week' || run.week !== wk) {
      return;
    }
    if (run.phase === 'swiss' && !run.me) {
      // Nobody of ours is in it: the Swiss is run through at once.
      resolveAll(run, false);
      this.afterChange(run);
    }
    if (run.phase === 'swiss' && run.me && t >= CUT_T) {
      forfeitMine(run);
      this.afterChange(run);
    }
    if (run.phase === 'swissDone' && t >= CUT_T) {
      startBracket(run);
      this.h.notice(`${TOURNEY_TEXT.name}: รอบคัดออกเริ่มแล้ว`);
      this.h.emit({ type: 'tourney', what: 'cut' });
      if (!run.me || !pendingFor(run, run.me)) {
        // The player is not in the bracket: it is played through.
        resolveAll(run, true);
      }
      this.afterChange(run);
    }
  }

  // ─────────────── the view ───────────────

  view(): TourneyView {
    const sv = this.save;
    const c = this.clock();
    const run = sv.run;
    const wk = this.weekNo();
    const t = hoursOf(c);
    const name = (id: number) => (run ? entrantOf(run, id).name : '');
    const bv = (r: BoutRec): TourneyBoutView => ({
      a: name(r.a),
      b: r.b === null ? null : name(r.b),
      aId: r.a,
      bId: r.b,
      winner: r.w,
      mine: !!run?.me && (r.a === run.me || r.b === run.me),
    });
    const signed = sv.signed === wk;
    let nextGate: TourneyView['nextGate'] = null;
    if (!run || run.mode === 'week') {
      if (!run || run.week < wk || run.phase === 'done' || sv.ran !== wk) {
        if (t < SWISS_T) nextGate = { label: 'รอบสวิสเริ่ม (เสาร์ 20:00)', hours: SWISS_T - t };
        else nextGate = { label: 'รอบสวิสเริ่มสัปดาห์หน้า (เสาร์ 20:00)', hours: 7 * 24 - t + SWISS_T };
      } else if (run.phase === 'swiss' || run.phase === 'swissDone') {
        if (t < CUT_T) nextGate = { label: 'รอบคัดออกเริ่ม (อาทิตย์ 20:00)', hours: CUT_T - t };
      }
    }
    const mineRec = run && run.me ? pendingFor(run, run.me) : null;
    const bye = !!run && run.phase === 'swiss' && !!run.me && run.pairs.some((r) => r.b === null && r.a === run.me);
    const canExhibit = bye && sv.exh !== `${run!.week}:${run!.swiss.round}`;
    const rows: TourneyRow[] = run
      ? swissStandings(run).map((r) => ({
          id: r.id,
          rank: r.rank,
          name: name(r.id),
          kind: entrantOf(run, r.id).kind,
          score: r.score,
          buchholz: r.buchholz,
          me: r.id === run.me,
        }))
      : [];
    return {
      stage: !run ? 'none' : run.phase,
      mode: run?.mode ?? null,
      week: wk + 1,
      day: c.day % 7,
      hour: c.hour,
      signupOpen: !this.sittingOut() && t < SIGNUP_END,
      signed,
      sittingOut: this.sittingOut(),
      nextGate,
      round: run ? run.swiss.round : 0,
      rounds: run ? run.swiss.rounds : 0,
      inField: !!run?.me,
      mine:
        run && mineRec && mineRec.b !== null
          ? {
              opponent: name(mineRec.a === run.me ? mineRec.b : mineRec.a),
              label: run.phase === 'swiss' ? `รอบสวิส ${run.swiss.round}/${run.swiss.rounds}` : `แพ้คัดออก รอบ ${run.bracket.length}`,
              kind: run.phase === 'swiss' ? 'swiss' : 'bracket',
            }
          : null,
      bye,
      canExhibit,
      standings: rows,
      pairs: run ? run.pairs.map(bv) : [],
      bracket: run ? run.bracket.map((r) => r.map(bv)) : [],
      places: run ? run.places.map(name) : [],
      myPlace: run && run.me ? placeOf(run, run.me) : 0,
      holder: sv.holder ?? null,
      hall: sv.hall,
      coins: sv.coins,
      titles: sv.titles,
      fighting: !!this.waiting,
    };
  }
}
